import React, { useState, useMemo } from 'react';
import { Modal, Typography, Icon, TextField } from '@ann/web-components';
import { useMediaQueries } from '../../../../../../../utils/mediaQueries';
import { ModalBodyContent, WarningIconContainer, MessageText, FieldContainer, ErrorMessage } from './DeactivateWorkspace.styled';

const DeactivateWorkspace = ({
  isOpen,
  onClose,
  onConfirm,
  workspaceName = '',
  isLoading = false
}) => {
  const [confirmText, setConfirmText] = useState('');
  const { isIpadAirPortrait, isIpadProPortrait } = useMediaQueries();
  
  // Calculate modal width: 90% for iPad portrait, 900px otherwise
  const modalWidth = useMemo(() => {
    if (isIpadAirPortrait || isIpadProPortrait) {
      return '90%';
    }
    return '900px';
  }, [isIpadAirPortrait, isIpadProPortrait]);
  
  const title = 'Deactivate Workspace';
  const message = 'Deactivating this workspace will remove user access and pause all active processes. This is a critical action.';

  const trimmedConfirm = confirmText.trim();
  const trimmedWorkspaceName = workspaceName.trim();

  const isFieldEmpty = !trimmedConfirm;
  const isNameMismatch = !!trimmedConfirm && trimmedConfirm !== trimmedWorkspaceName;
  const showError = isFieldEmpty || isNameMismatch;

  const isConfirmValid = !!trimmedConfirm && trimmedConfirm === trimmedWorkspaceName;
  const canConfirm = isConfirmValid && !isLoading;

  const handleClose = () => {
    if (!isLoading) {
      setConfirmText('');
      onClose();
    }
  };

  const handleConfirm = () => {
    if (canConfirm && onConfirm) {
      onConfirm();
    }
  };

  const handleConfirmTextChange = (value) => {
    setConfirmText(value);
  };

  // Reset confirm text when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setConfirmText('');
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} width={modalWidth} size="medium">
      <Modal.Header>
        <Modal.Header.Title/>      
      </Modal.Header>
      <Modal.Body>
        <ModalBodyContent>
          <WarningIconContainer>
            <Icon color="alert" name="warning" size="large"/>
          </WarningIconContainer>
          <MessageText>
            <Typography category="title" size="medium" weight="bold">
              {title}
            </Typography>
            <Typography category="body" size="medium" weight="regular" color="body">
              {message}
            </Typography>
            <FieldContainer>
              <TextField
                label="To confirm, enter the workspace name below."
                onChange={handleConfirmTextChange}
                size="small"
                placeholder="Confirm by typing the workspace name"  
                required={true}
                value={confirmText}
              />
            </FieldContainer>
            {showError && (
              <ErrorMessage>
                {isFieldEmpty
                  ? 'Workspace name is required'
                  : 'Please enter the exact workspace name to confirm'}
              </ErrorMessage>
            )}
          </MessageText>
        </ModalBodyContent>
      </Modal.Body>
      <Modal.Actions align="center">
        <Modal.Button
          label="Cancel"
          onClick={handleClose}
          type="secondary"
          disabled={isLoading}
        />
        <Modal.Button
          label="Deactivate"
          onClick={handleConfirm}
          type="error"
          disabled={!canConfirm}
          isLoading={isLoading}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default DeactivateWorkspace;

